import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "pagewise_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000, // 30s ceiling so requests can't hang forever during cold-start
});

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// ---- Auto-retry transient failures (pod cold-start, gateway 5xx) ----
// We retry on:
//   - Network errors (no `response` object — pod sleeping/CORS/timeout)
//   - HTTP 502/503/504 (kubelet rolling, backend still booting)
// We do NOT retry 4xx (those are real client errors, e.g., wrong password).
//
// Up to 4 attempts total with backoff (1s, 2s, 3s) — covers ~6 s of cold-start.
const RETRY_DELAYS_MS = [1000, 2000, 3000];
const RETRY_STATUSES = new Set([502, 503, 504]);

api.interceptors.response.use(
  (r) => r,
  async (e) => {
    const cfg = e.config || {};
    const status = e.response?.status;
    const isTransient = !e.response || RETRY_STATUSES.has(status);
    if (!isTransient) return Promise.reject(e);
    cfg.__retryCount = (cfg.__retryCount || 0);
    if (cfg.__retryCount >= RETRY_DELAYS_MS.length) return Promise.reject(e);
    const delay = RETRY_DELAYS_MS[cfg.__retryCount];
    cfg.__retryCount += 1;
    await new Promise((r) => setTimeout(r, delay));
    try {
      return await api.request(cfg);
    } catch (e2) {
      return Promise.reject(e2);
    }
  }
);

// ---- Proactive warmup ----
// Fire a cheap health request on app load to wake the backend BEFORE the user
// tries to log in. Also retried up to 3 times by the interceptor above.
let warmedUp = false;
export function warmupBackend() {
  if (warmedUp) return Promise.resolve();
  warmedUp = true;
  return api.get("/health").catch(() => {});
}
